<template>
  <header class="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md shadow-sm z-[1001] border-b border-blue-100">
    <div class="flex justify-between items-center max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
      <router-link 
        to="/" 
        class="flex items-center space-x-3 group"
      >
        <img 
          src="@/assets/logo.webp" 
          alt="Success Boost Logo"
          class="h-6 w-auto transition-transform duration-300 group-hover:scale-105"
        >
        <h1 class="text-blue-900 text-xl font-bold font-sans tracking-tight group-hover:text-yellow-500 transition-colors duration-300">Success Boost</h1>
      </router-link>

      <!-- Desktop Navigation -->
      <nav class="hidden md:flex items-center space-x-8">
        <template v-if="!loggedIn">
          <router-link 
            v-for="item in desktopGuestNav" 
            :key="item.path" 
            :to="item.path"
            class="text-blue-900 hover:text-yellow-500 font-medium transition-colors duration-200"
            :class="isActive(item.path) ? 'text-yellow-600 font-bold' : ''"
          >
            {{ item.label }}
          </router-link>
        </template>
        <template v-else>
          <router-link 
            v-for="item in desktopUserNav" 
            :key="item.path" 
            :to="item.path"
            class="text-blue-900 hover:text-yellow-500 font-medium transition-colors duration-200"
            :class="isActive(item.path) ? 'text-yellow-600 font-bold' : ''"
          >
            {{ item.label }}
          </router-link>
        </template>
      </nav>

      <!-- Auth Buttons (Desktop) -->
      <div class="hidden md:flex items-center space-x-4">
        <template v-if="!loggedIn">
          <router-link 
            to="/login" 
            class="text-blue-900 hover:text-yellow-500 font-medium transition-colors"
          >
            Log In
          </router-link>
          <router-link 
            to="/register" 
            class="px-4 py-2 bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-md hover:from-blue-800 hover:to-blue-600 transition-colors font-medium"
          >
            Get Started
          </router-link>
        </template>
        <template v-else>
          <!-- User profile button -->
          <router-link 
            to="/profile" 
            class="flex items-center space-x-2 group"
          >
            <img 
              v-if="profile?.profilePicture"
              :src="profile.profilePicture"
              alt="Profile"
              class="h-8 w-8 rounded-full object-cover border-2 border-yellow-500 group-hover:border-yellow-400 transition-all duration-200"
            />
            <span
              v-else
              class="h-8 w-8 rounded-full border-2 border-yellow-500 bg-blue-100 text-blue-800 flex items-center justify-center text-xs font-semibold group-hover:border-yellow-400 transition-all duration-200"
            >
              {{ profileInitials }}
            </span>
            <span class="text-blue-900 font-medium group-hover:text-yellow-500 transition-colors">
              {{ profile?.name || 'Account' }}
            </span>
          </router-link>
          <button 
            @click="handleLogout"
            class="text-blue-900 hover:text-red-600 font-medium transition-colors flex items-center space-x-1"
          >
            <span class="material-symbols-outlined text-sm">logout</span>
            <span>Log Out</span>
          </button>
        </template>
      </div>
      
      <!-- Mobile Menu Button -->
      <div class="relative md:hidden">
        <button 
          class="flex items-center justify-center h-9 w-9 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-900 hover:text-yellow-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:ring-offset-2 focus:ring-offset-white/90"
          @click.stop="toggleMenu"
          aria-label="Menu"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
          </svg>
        </button>
        
        <transition name="dropdown">
          <div 
            v-if="isMenuOpen"
            class="absolute right-0 mt-2 w-56 origin-top-right bg-white rounded-xl shadow-xl py-2 ring-1 ring-black ring-opacity-5 z-50 border border-blue-100"
            v-click-outside="closeMenu"
          >
            <!-- Mobile Menu Content -->
            <template v-if="!loggedIn">
              <!-- Guest Menu Items -->
              <div 
                v-for="(option, index) in mobileGuestNav" 
                :key="index"
                class="flex items-center px-4 py-3 text-sm text-blue-900 hover:bg-blue-50 cursor-pointer transition-colors duration-200"
                @click="navigateTo(option.path)"
              >
                <span 
                  class="flex items-center justify-center w-5 h-5 mr-3"
                  :class="option.iconClass || 'text-yellow-500'"
                >
                  <span class="material-symbols-outlined">{{ option.icon }}</span>
                </span>
                <span>{{ option.label }}</span>
              </div>
              <!-- Auth buttons for mobile -->
              <div class="border-t border-blue-100 mt-2 pt-2">
                <div 
                  class="flex items-center px-4 py-3 text-sm text-blue-900 hover:bg-blue-50 cursor-pointer transition-colors duration-200"
                  @click="navigateTo('/login')"
                >
                  <span class="flex items-center justify-center w-5 h-5 mr-3 text-blue-900">
                    <span class="material-symbols-outlined">login</span>
                  </span>
                  <span>Log In</span>
                </div>
                <div 
                  class="flex items-center px-4 py-3 text-sm text-blue-900 hover:bg-blue-50 cursor-pointer transition-colors duration-200"
                  @click="navigateTo('/register')"
                >
                  <span class="flex items-center justify-center w-5 h-5 mr-3 text-yellow-500">
                    <span class="material-symbols-outlined">person_add</span>
                  </span>
                  <span>Get Started</span>
                </div>
              </div>
            </template>
            <template v-else>
              <!-- Logged-in User Menu Items -->
              <div 
                v-for="(option, index) in mobileUserNav" 
                :key="index"
                class="flex items-center px-4 py-3 text-sm text-blue-900 hover:bg-blue-50 cursor-pointer transition-colors duration-200"
                @click="navigateTo(option.path)"
              >
                <span 
                  class="flex items-center justify-center w-5 h-5 mr-3"
                  :class="option.iconClass || 'text-yellow-500'"
                >
                  <span class="material-symbols-outlined">{{ option.icon }}</span>
                </span>
                <span>{{ option.label }}</span>
              </div>
              <!-- Logout for mobile -->
              <div class="border-t border-blue-100 mt-2 pt-2">
                <div 
                  class="flex items-center px-4 py-3 text-sm text-red-600 hover:bg-red-50 cursor-pointer transition-colors duration-200"
                  @click="handleLogout"
                >
                  <span class="flex items-center justify-center w-5 h-5 mr-3 text-red-500">
                    <span class="material-symbols-outlined">logout</span>
                  </span>
                  <span>Log Out</span>
                </div>
              </div>
            </template>
          </div>
        </transition>
      </div>
    </div>
  </header>
  <div class="header-space h-16"></div>
</template>

<script>
import { mapState } from 'vuex';
import { logoutUser } from '@/composables/useAuth';

export default {
  name: 'AppHeader',
  data() {
    return {
      isMenuOpen: false,
      desktopGuestNav: [
        { label: 'Resources', path: '/resources' },
      ],
      desktopUserNav: [
        { label: 'Timetable', path: '/timetable' },
        { label: 'Bus', path: '/bus' },
        { label: 'Resources', path: '/resources' },
        { label: 'Socials', path: '/socials' },
      ],
      mobileGuestNav: [
        { label: 'Resources', path: '/resources', icon: 'auto_stories', iconClass: 'text-green-500' },
      ],
      mobileUserNav: [
        { label: 'Timetable', path: '/timetable', icon: 'schedule', iconClass: 'text-blue-500' },
        { label: 'Bus', path: '/bus', icon: 'departure_board', iconClass: 'text-amber-500' },
        { label: 'Resources', path: '/resources', icon: 'auto_stories', iconClass: 'text-green-500' },
        { label: 'Socials', path: '/socials', icon: 'diversity_1', iconClass: 'text-purple-500' },
        { label: 'Profile', path: '/profile', icon: 'account_circle', iconClass: 'text-indigo-500' },
      ]
    }
  },
  computed: {
    ...mapState(['user', 'profile']),
    loggedIn() {
      return !!this.user;
    },
    profileInitials() {
      const name = this.profile?.name || this.user?.email || 'Account';
      return name
        .split(' ')
        .filter(Boolean)
        .map(part => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase() || 'A';
    }
  },
  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
    },
    closeMenu() {
      this.isMenuOpen = false
    },
    navigateTo(path) {
      this.closeMenu()
      this.$router.push(path)
    },
    async handleLogout() {
      try {
        await logoutUser();
        this.$router.push('/login');
      } catch (error) {
        console.error("Logout error:", error);
      }
    },
    isActive(path) {
      if (path === '/socials') {
        return ['/socials', '/posts', '/chat', '/hustleup'].some(route => this.$route.path.startsWith(route));
      }
      return this.$route.path.startsWith(path);
    },
  },
  directives: {
    'click-outside': {
      mounted(el, binding) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event)
          }
        }
        document.body.addEventListener('click', el.clickOutsideEvent)
      },
      unmounted(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent)
      }
    }
  }
}
</script>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

header {
  height: 64px;
}
</style>
